import * as React from 'react';
import * as _ from 'lodash';
import * as styles from './add-global-document.module.scss';
import PrimaryButton from '../../common/primary-button';
import * as commonStyles from '../../common/common.module.scss';
import PriorityTwoFailure from '../../announcement/priority-two-failure';
import PriorityTwoSuccess from '../../announcement/priority-two-success';
import TinyEditor from '../../common/TinyEditor';
import OutlinedInput from '../../common/outlined-input';
import OutlinedSelect from '../../common/outlined-select';
import { Formik, FormikConsumer } from 'formik';
import * as Yup from 'yup';
import useHelpDocumentManagement from '../../../hooks/useHelpDocumentManagement';
import TertiaryButton from '../../common/tertiary-button';
import Paths from '../../../models/enums/paths';
import { useRef, Fragment } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import useTimedAlerts from '../../../hooks/useTimedAlerts';
import Tag from '../../../models/tag';
import TransferList from '../../common/transfer-list';
import Dashboard from '../../../models/dashboard';
import HelpDocumentManagement from '../../../models/helpDocumentManagement';
import { CircularProgress } from '@material-ui/core';
import tinyMceContentCssUrl from 'url:./help-document-editor.css';
import NavigationWarning from '../navigationWarning';

interface Props {
  document: HelpDocumentManagement;
  isNewDocument?: boolean;
  isGlobal?: boolean;
  loading?: boolean;
}

const ManageHelpDocument = ({ document, isNewDocument, isGlobal, loading }: Props) => {
  const { clientId, revisions } = useParams<{ clientId: string; revisions: string }>();
  const history = useHistory();
  const ManageDocumentSchema = Yup.object().shape({
    title: Yup.string().required('Name is a required field.')
  });

  const tinyMcePlugins =
    'autolink fullscreen image link media charmap insertdatetime lists advlist wordcount help code searchreplace table';

  const tinyMceToolbar =
    'insertfile formatselect | bold italic underline strikethrough forecolor backcolor | link | alignleft aligncenter alignright alignjustify  | numlist bullist outdent indent  | removeformat';

  const {
    saveGlobalDocument,
    saveClientDocument,
    publishClientDocument,
    publishGlobalDocument,
    fetchAssociatedData,
    loadingAssociatedData,
    tags,
    dashboards,
    successMessage,
    setSuccessMessage,
    activeDocumentId
  } = useHelpDocumentManagement();

  const [unassignedTags, setUnassignedTags] = React.useState<Tag[]>(tags);
  const [assignedTags, setAssignedTags] = React.useState<Tag[]>([]);
  const [unassignedDashboards, setUnassignedDashboards] = React.useState<Dashboard[]>(dashboards);
  const [assignedDashboards, setAssignedDashboards] = React.useState<Dashboard[]>([]);

  const [displayDirtyWarning, setDisplayDirtyWarning] = React.useState<boolean>(false);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState<boolean>(false);
  const [blockedLocation, setBlockedLocation] = React.useState<string>('');
  const [initialValues, setInitialValues] = React.useState<HelpDocumentManagement>(document);
  const [isPublishing, setIsPublishing] = React.useState<boolean>(false);
  const { errorMessage, setErrorMessage } = useTimedAlerts();
  const [isDebouncing, setIsDebouncing] = React.useState<boolean>(false);
  const [newDocument, setNewDocument] = React.useState<boolean>(isNewDocument);

  const primaryEditorRef = React.useRef(null);
  const alternativeEditorRef = React.useRef(null);

  const unblock = useRef(null);

  const formikRef = useRef(null);

  React.useEffect(() => {
    if (clientId) {
      fetchAssociatedData(Number(clientId));
    }
    unblock.current = history.block((location) => {
      if (formikRef.current !== null && formikRef.current.dirty) {
        setDisplayDirtyWarning(true);
        setBlockedLocation(location.pathname);
        return false;
      } else {
        unblock.current();
      }
    });
  }, []);

  React.useEffect(() => {
    setInitialValues(document);

    if (clientId) {
      if (!loadingAssociatedData) {
        setAssignedDashboards(document.dashboards);
        setAssignedTags(document.tags);
        const loadedDashboards = dashboards.filter(
          (dashboard) => !document.dashboards.map((dash) => dash.reportId).includes(dashboard.reportId)
        );
        setUnassignedDashboards(loadedDashboards);

        const loadedTags = tags.filter((tag) => !document.tags.map((dash) => dash.tagId).includes(tag.tagId));
        setUnassignedTags(loadedTags);
      }
    }
  }, [document, loadingAssociatedData]);

  React.useEffect(() => {
    if (confirmedNavigation) {
      unblock.current();
      history.push(blockedLocation);
    }
  }, [confirmedNavigation]);

  React.useEffect(() => {
    if (isPublishing && !initialValues.isPublished) {
      handlePublish(initialValues.snippetId);
    }
  }, [initialValues]);

  const handleCancel = (dirty: boolean) => {
    cancelAutoSave();
    if (!dirty) {
      unblock.current();
    }
    if (clientId) {
      if (revisions) {
        history.push(
          Paths.ViewClientHelpDocumentRevisions.replace(':clientId', clientId).replace(
            ':snippetGroupId',
            initialValues.snippetGroupId.toString()
          )
        );
      } else {
        history.push(`${Paths.ClientHelpDocuments.replace(':clientId', clientId)}/${activeDocumentId || ''}`);
      }
    } else {
      if (revisions) {
        history.push(
          Paths.ViewGlobalHelpDocumentRevisions.replace(':snippetGroupId', initialValues.snippetGroupId.toString())
        );
      } else {
        history.push(`${Paths.GlobalHelpDocuments}/${activeDocumentId || ''}`);
      }
    }
  };

  const handleGlobalPublish = async (snippetId: number) => {
    try {
      const response = await publishGlobalDocument(snippetId);
      if (response.success) {
        setSuccessMessage(response.message);
        setInitialValues({ ...initialValues, isPublished: true });
        if (!isNewDocument) {
          history.replace(
            `${Paths.EditGlobalHelpDocument.replace(':snippetId', snippetId.toString())}${
              revisions ? '/revisions' : ''
            }`
          );
        }
      } else {
        setErrorMessage(response.message);
      }
    } catch (err) {
      setErrorMessage(err.responseJSON.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handleClientPublish = async (snippetId: number) => {
    try {
      const response = await publishClientDocument(Number(clientId), snippetId);
      if (response.success) {
        setSuccessMessage(response.message);
        setInitialValues({ ...initialValues, isPublished: true });
        if (!isNewDocument) {
          history.replace(
            `${Paths.EditClientHelpDocument.replace(':clientId', clientId).replace(
              ':snippetId',
              snippetId.toString()
            )}${revisions ? '/revisions' : ''}`
          );
        }
      } else {
        setErrorMessage(response.message);
      }
    } catch (err) {
      setErrorMessage(err.responseJSON.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const handlePublish = (snippetId: number) => {
    if (isGlobal) {
      handleGlobalPublish(snippetId);
    } else {
      handleClientPublish(snippetId);
    }
  };

  const onSubmitSuccess = (documentSubmitted: HelpDocumentManagement, response: HelpDocumentManagement) => {
    const name = documentSubmitted.title || `Document ${response.snippetGroupId}`;
    const action = newDocument ? 'added' : 'updated';
    setSuccessMessage(`${name} has been ${action}`);
    setInitialValues({
      ...document,
      snippetId: response.snippetId,
      snippetGroupId: response.snippetGroupId,
      isPublished: response.isPublished,
      revision: response.revision
    });

    if (newDocument) {
      setNewDocument(false);
    }
  };

  const callSubmitFunction = React.useCallback((func) => func(), []);

  const startDebounce = React.useCallback(_.debounce(callSubmitFunction, 30000), []);

  const startAutoSave = (handleSubmitFunction: () => void) => {
    setIsDebouncing(true);
    startDebounce(handleSubmitFunction);
  };

  const cancelAutoSave = () => {
    setIsDebouncing(false);
    startDebounce.cancel();
  };

  return loading ? (
    <div className="loadingIndicator">
      <CircularProgress />
    </div>
  ) : (
    <div>
      <div className={styles.header}>
        <h1>
          {newDocument ? 'Add' : 'Edit'} {clientId ? 'Client' : 'Global'} Document - Revision #{initialValues.revision}
        </h1>
      </div>
      {errorMessage && <PriorityTwoFailure message={errorMessage} />}
      {successMessage && <PriorityTwoSuccess message={successMessage} />}
      <Formik
        innerRef={formikRef}
        enableReinitialize
        initialValues={initialValues}
        validationSchema={ManageDocumentSchema}
        onSubmit={(doc, { resetForm }) => {
          const docCopy = _.cloneDeep(doc);
          doc.isPublished = false;
          if (doc.feature === 'None') {
            doc.feature = '';
          }
          const contentChanged = doc.content !== initialValues.content;
          const alternativeChanged = doc.alternative !== initialValues.alternative;

          doc.contentChanged = contentChanged;
          doc.alternativeChanged = alternativeChanged;
          if (!contentChanged) {
            doc.content = null;
          }
          if (!alternativeChanged) {
            doc.alternative = null;
          }
          if (!clientId || (clientId && isGlobal && !newDocument)) {
            doc.titleChanged = true;
            doc.dashboardChanged = false;
            doc.tagsChanged = false;
            doc.featureChanged = true;

            if (clientId && isGlobal && !newDocument) {
              doc.content = null;
              doc.alternative = null;
              doc.clientId = Number(clientId);
              doc.titleChanged = false;
              doc.dashboardChanged = true;
              doc.tagsChanged = true;
            }

            saveGlobalDocument(doc)
              .then((response) => {
                docCopy.revision = response.revision;
                docCopy.snippetId = response.snippetId;
                onSubmitSuccess(docCopy, response);
                resetForm();
              })
              .catch((err) => {
                setErrorMessage(err.responseJSON.message);
                if (isPublishing) {
                  setIsPublishing(false);
                }
              });
          } else {
            doc.contentChanged = contentChanged;
            doc.alternativeChanged = alternativeChanged;
            doc.titleChanged = true;
            doc.dashboardChanged = true;
            doc.tagsChanged = true;
            doc.featureChanged = true;

            saveClientDocument(doc, clientId)
              .then((response) => {
                docCopy.revision = response.revision;
                docCopy.snippetId = response.snippetId;
                onSubmitSuccess(docCopy, response);
                resetForm();
              })
              .catch((err) => {
                setErrorMessage(err.responseJSON.message);
              });
          }
        }}
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, dirty, isValid }) => {
          const contentIsDirty =
            values.title !== initialValues.title ||
            values.content !== initialValues.content ||
            values.alternative !== initialValues.alternative;
          if ((!dirty || !isValid) && isDebouncing) {
            cancelAutoSave();
          }
          return (
            <form>
              <FormikConsumer>
                {(formik) => (
                  <NavigationWarning
                    formik={formik}
                    displayDirtyWarning={displayDirtyWarning}
                    confirmNavigationWarning={() => {
                      setConfirmedNavigation(true);
                      history.push(blockedLocation);
                    }}
                    cancleNavigationWarning={() => {
                      startAutoSave(handleSubmit);
                      setDisplayDirtyWarning(false);
                    }}
                  />
                )}
              </FormikConsumer>
              {((!isGlobal && clientId) || (isGlobal && !clientId)) && (
                <React.Fragment>
                  <OutlinedInput
                    id="title"
                    label="Name"
                    name="title"
                    onChange={(event) => {
                      startAutoSave(handleSubmit);
                      handleChange(event);
                    }}
                    onBlur={handleBlur}
                    onKeyDown={(event) => {
                      // Prevents submit when pressing enter on title input
                      if (event.keyCode === 13) {
                        event.preventDefault();
                      }
                    }}
                    value={values.title || ''}
                    error={errors.title && touched.title}
                    helperText={errors.title && touched.title && errors.title}
                  />
                  <fieldset className={`${commonStyles.fieldset}`}>
                    <h5>Main Text</h5>
                    <TinyEditor
                      editorRef={primaryEditorRef}
                      init={{
                        content_css: tinyMceContentCssUrl
                      }}
                      initialValue={initialValues.content || ''}
                      setFieldValue={(field: string, value: any, shouldValidate?: boolean) => {
                        startAutoSave(handleSubmit);
                        setFieldValue(field, value, shouldValidate);
                      }}
                      setField="content"
                      plugins={tinyMcePlugins}
                      toolbar={tinyMceToolbar}
                    />
                  </fieldset>
                  <fieldset className={`${commonStyles.fieldset}`}>
                    <h5>Alternative Text</h5>
                    <TinyEditor
                      editorRef={alternativeEditorRef}
                      initialValue={initialValues.alternative || ''}
                      setFieldValue={(field: string, value: any, shouldValidate?: boolean) => {
                        startAutoSave(handleSubmit);
                        setFieldValue(field, value, shouldValidate);
                      }}
                      setField="alternative"
                      plugins={tinyMcePlugins}
                      toolbar={tinyMceToolbar}
                    />
                  </fieldset>
                </React.Fragment>
              )}
              {clientId && (
                <fieldset className={`${commonStyles.fieldset}`}>
                  <h5>Permission Tags</h5>
                  <TransferList
                    idField="tagId"
                    labelField="name"
                    left={unassignedTags}
                    right={assignedTags}
                    setLeft={setUnassignedTags}
                    setRight={setAssignedTags}
                    setFieldValue={(field: string, value: any, shouldValidate?: boolean) => {
                      startAutoSave(handleSubmit);
                      setFieldValue(field, value, shouldValidate);
                    }}
                    setField="tags"
                  />
                </fieldset>
              )}
              {clientId && (
                <fieldset className={`${commonStyles.fieldset}`}>
                  <h5>Dashboards</h5>
                  <TransferList
                    idField="reportId"
                    labelField="name"
                    left={unassignedDashboards}
                    right={assignedDashboards}
                    setLeft={setUnassignedDashboards}
                    setRight={setAssignedDashboards}
                    setFieldValue={(field: string, value: any, shouldValidate?: boolean) => {
                      startAutoSave(handleSubmit);
                      setFieldValue(field, value, shouldValidate);
                    }}
                    setField="dashboards"
                  />
                </fieldset>
              )}
              {((clientId && !isGlobal) || (isGlobal && !clientId)) && (
                <fieldset className={`${commonStyles.fieldset}`}>
                  <OutlinedSelect
                    id="feature"
                    title="Assigned Feature"
                    value={values.feature || 'None'}
                    handleChange={(event) => {
                      startAutoSave(handleSubmit);
                      handleChange(event);
                    }}
                    handleBlur={handleBlur}
                    options={
                      <Fragment>
                        <option key="none" value="None">
                          None
                        </option>
                        <option key="Tableau" value="Tableau">
                          Tableau
                        </option>
                      </Fragment>
                    }
                  />
                </fieldset>
              )}
              <div className={commonStyles.formActions}>
                <TertiaryButton text="Back" title="Back" handleClick={() => handleCancel(dirty)} />
                <div className={styles.primaryFormActions}>
                  <div className={commonStyles.submitButton}>
                    <PrimaryButton
                      text="Publish"
                      title="Publish"
                      deactive={!isValid || (initialValues.isPublished && !contentIsDirty)}
                      handleClick={(event) => {
                        cancelAutoSave();
                        if (!dirty) {
                          handlePublish(values.snippetId);
                        } else {
                          setIsPublishing(true);
                          handleSubmit(event);
                        }
                      }}
                    />
                  </div>
                  <div className={commonStyles.submitButton}>
                    <PrimaryButton
                      text="Save"
                      title="Save"
                      deactive={!dirty || !isValid}
                      handleClick={(event) => {
                        cancelAutoSave();
                        handleSubmit(event);
                      }}
                    />
                  </div>
                </div>
              </div>
            </form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ManageHelpDocument;
